import { HirerBudgetUpsellConnectedCart } from '@seek/hirer-budget-upsell';
import { useHubble } from '@seek/hubble';
import { useMelwaysInfo } from '@seek/melways-react';
import { Bleed } from 'braid-design-system';
import { useCallback } from 'react';

import { SHOPPING_CART_SUBSCRIPTION_RESPONSE_KEY } from 'src/app/shared/constants/shoppingCart';
import {
  CAJA_FLOW_TYPE_CREATE,
  HIRER_PURCHASE_TYPE_CASUAL,
  HIRER_PURCHASE_TYPE_CONTRACT,
} from 'src/app/shared/constants/tealium';
import { saveTrackingData } from 'src/app/shared/dataAnalytics/storage';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { log } from 'src/app/utils/logging';
import { useAnalyticsTracking } from 'src/app/v2/hooks/useAnalyticsTracking';
import { useHirerAccount } from 'src/app/v2/hooks/useHirerAccount';
import { usePage } from 'src/app/v2/hooks/usePage';
import { useQueryParam } from 'src/app/v2/hooks/useQueryParam';
import { GetAdProductsForAdPostingDocument } from 'src/graphql/generated';
import type { TrackingEvent } from 'src/types/trackingData';

export const AdbudgetUpsellSection: React.FC = () => {
  const { trackingAdapter } = useAnalyticsTracking();
  const draftId = useQueryParam('draftid');
  const hirerAccount = useHirerAccount();
  const { locale } = useMelwaysInfo();
  const hubble = useHubble();
  const pageId = usePage();

  const { cartState: { productSetId } = {} } = useShoppingCart();
  const getProductsDocumentConstructVariables = useCallback(
    (productSetIdForInput: string) => ({
      input: { productSetId: productSetIdForInput, locale },
    }),
    [locale],
  );

  const shoppingCartProps = {
    getProductsDocument: GetAdProductsForAdPostingDocument,
    getProductsDocumentConstructVariables,
    key: 'BudgetUpsellPanel',
    subscriptionResponseKey: SHOPPING_CART_SUBSCRIPTION_RESPONSE_KEY,
  };

  const onAdBudgetSelected = (
    budgetProductId: string | null,
    budgetName: string | null,
  ) => {
    saveTrackingData({
      contractProductSetProductId: budgetProductId,
      contractProductName: budgetName,
      adBudget: { id: budgetProductId },
    });
  };

  const onBudgetOptionClicked = (
    budgetValue: number,
    interactionType: string,
  ) => {
    const adbudgetValueInDollars = budgetValue / 100;
    saveTrackingData({
      adBudgetValue:
        interactionType === 'select'
          ? adbudgetValueInDollars.toString()
          : undefined,
      hirerPurchaseType:
        interactionType === 'select'
          ? HIRER_PURCHASE_TYPE_CONTRACT
          : HIRER_PURCHASE_TYPE_CASUAL,
    });
  };

  const onError = (error: Error) => {
    log.error(
      'AdBudgetUpsell | Upsell | onError',
      {
        error,
        draftId,
        seekAdvertiserId: hirerAccount?.id,
      },
      true,
    );
  };

  if (!hirerAccount?.nation) return <Bleed space="xlarge">{null}</Bleed>;

  return (
    <HirerBudgetUpsellConnectedCart
      opportunityId={draftId!}
      locale={locale}
      country={hirerAccount?.nation}
      onSelectedBudgetChange={onAdBudgetSelected}
      onBudgetOptionClicked={onBudgetOptionClicked}
      onTrackingCallback={(eventData) => {
        trackingAdapter.sendLinkEvent(eventData as TrackingEvent);
      }}
      hubbleTrackEventHandler={(eventName: any, eventData: any) => {
        hubble.trackEvent(eventName, {
          ...eventData,
          draftId,
          postingFlowType: CAJA_FLOW_TYPE_CREATE,
          postingFlowPageName: pageId,
          productSetId,
          opportunityId: draftId,
        });
      }}
      logger={log}
      onError={onError}
      useUnifiedTracking={true}
      shoppingCart={shoppingCartProps}
    />
  );
};
